import { faCalendar, faCreditCard, faEye, faMegaphone, faPen } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IPutTeeSheetSettings } from "api/rpc/2024-04/facilityAdmin/facility/settings";
import ButtonGroup from "components/buttonGroup/ButtonGroup";
import React from "react";
import { useTranslation } from "react-i18next";
import "../teeSheetGeneral.scss";
import Divider from "components/divider";
import useModal from "hooks/modals/useModal";
import Markdown from "components/markdown/Markdown";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import TextEditor from "components/textEditor/textEditor";

interface IProps {
    state: IPutTeeSheetSettings;
    updateState: <K extends keyof IPutTeeSheetSettings>(key: K, value: IPutTeeSheetSettings[K]) => void;
}

export default function MarketingAndTerms(props: IProps) {
    const {state, updateState } = props;
    const {t} = useTranslation();
    const {state: bookingTermsState, updateModal: updateBookingTermsState, closeModal: closeBookingTermsState} = useModal<{buttonKey: string; terms: string}>({terms: "", buttonKey: "none"});
    const {state: paymentTermsState, updateModal: updatePaymentTermsState, closeModal: closePaymentTermsState} = useModal<{buttonKey: string; terms: string}>({terms: "", buttonKey: "none"});
    const {state: marketingState, updateModal: updateMarketingState, closeModal: closeMarketingState} = useModal<{buttonKey: string; terms: string}>({terms: "", buttonKey: "none"});

    function onButtonClick(key: string, type: "booking-terms" | "payment-terms" | "marketing") {
        if (type === "booking-terms") {
            void updateBookingTermsState({buttonKey: key, terms: state?.booking_terms, isOpen: key === "edit" ? true : undefined});
        } else if (type === "payment-terms") {
            void updatePaymentTermsState({buttonKey: key, terms: state?.payment_terms, isOpen: key === "edit" ? true : undefined});
        } else {
            void updateMarketingState({buttonKey: key, terms: state?.marketing_section, isOpen: key === "edit" ? true : undefined});
        }
    }

    return (
        <div className="p-2">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="ui-tee-sheet-general-marketing-icon">
                        <FontAwesomeIcon icon={faCalendar}/>
                    </div>
                    <p className="ui-tee-sheet-general-marketing-title">{t("Booking Terms")}</p>
                </div>
                <ButtonGroup onButtonClick={key => onButtonClick(key as string, "booking-terms")} startKey={bookingTermsState?.buttonKey} buttons={[{label: t("Edit"), key: "edit", icon: faPen}, {label: t("Preview"), key: "preview", icon: faEye}]}/>
            </div>
            {bookingTermsState?.buttonKey === "preview" ? (
                <div className="ui-tee-sheet-general-marketing-preview">
                    <Markdown markdownText={state?.booking_terms}/>
                </div>
            ) : null}
            <Divider/>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="ui-tee-sheet-general-marketing-icon">
                        <FontAwesomeIcon icon={faCreditCard}/>
                    </div>
                    <p className="ui-tee-sheet-general-marketing-title">{t("Payment Terms")}</p>
                </div>
                <ButtonGroup onButtonClick={key => onButtonClick(key as string, "payment-terms")} startKey={paymentTermsState?.buttonKey} buttons={[{label: t("Edit"), key: "edit", icon: faPen}, {label: t("Preview"), key: "preview", icon: faEye}]}/>
            </div>
            {paymentTermsState?.buttonKey === "preview" ? (
                <div className="ui-tee-sheet-general-marketing-preview">
                    <Markdown markdownText={state?.payment_terms}/>
                </div>
            ) : null}
            <Divider/>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="ui-tee-sheet-general-marketing-icon">
                        <FontAwesomeIcon icon={faMegaphone}/>
                    </div>
                    <p className="ui-tee-sheet-general-marketing-title">{t("Marketing Section")}</p>
                </div>
                <ButtonGroup onButtonClick={key => onButtonClick(key as string, "marketing")} startKey={marketingState?.buttonKey} buttons={[{label: t("Edit"), key: "edit", icon: faPen}, {label: t("Preview"), key: "preview", icon: faEye}]}/>
            </div>

            {marketingState?.buttonKey === "preview" ? (
                <div className="ui-tee-sheet-general-marketing-preview">
                    <Markdown markdownText={state?.marketing_section}/>
                </div>
            ) : null}

            <Portal isMounted={bookingTermsState?.isOpen}>
                <Sheet closable title={t("Edit Booking Terms")} size="small" open={bookingTermsState?.isOpen} onCancel={closeBookingTermsState} onOk={() => {
                    void updateState("booking_terms", bookingTermsState?.terms); 
                    void closeBookingTermsState();
                    }} okText={t("Confirm Changes")}>
                    <TextEditor
                    markdownText={bookingTermsState?.terms || ""}
                    markdownTextOnChange={text => updateBookingTermsState({terms: text})}
                />
                </Sheet>
            </Portal>

            <Portal isMounted={paymentTermsState?.isOpen}>
                <Sheet closable title={t("Edit Payment Terms")} size="small" open={paymentTermsState?.isOpen} onCancel={closePaymentTermsState} onOk={() => {
                    void updateState("payment_terms", paymentTermsState?.terms); 
                    void closePaymentTermsState();
                    }} okText={t("Confirm Changes")}>
                    <TextEditor
                    markdownText={paymentTermsState?.terms || ""}
                    markdownTextOnChange={text => updatePaymentTermsState({terms: text})}
                />
                </Sheet>
            </Portal>

            <Portal isMounted={marketingState?.isOpen}>
                <Sheet closable title={t("Edit Marketing Section")} size="small" open={marketingState?.isOpen} onCancel={closeMarketingState} onOk={() => {
                    void updateState("marketing_section", marketingState?.terms); 
                    void closeMarketingState();
                    }} okText={t("Confirm Changes")}>
                    <TextEditor
                    markdownText={marketingState?.terms || ""}
                    markdownTextOnChange={text => updateMarketingState({terms: text})}
                />
                </Sheet>
            </Portal>
        </div>
    );
}